import { useEffect, useRef, useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import DownOutlined from '@ant-design/icons/DownOutlined';
import CheckOutlined from '@ant-design/icons/CheckOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import TeamOutlined from '@ant-design/icons/TeamOutlined';

import { listWorkspaces, createWorkspace, getActiveWorkspaceId, setActiveWorkspaceId, type Workspace } from 'api/workspaces';
import { openSnackbar } from 'api/snackbar';

// ==============================|| HEADER - SELETOR DE GESTÃO ||============================== //

export default function WorkspaceSwitcher() {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Workspace[]>([]);
  const [activeId, setActiveId] = useState<string | null>(getActiveWorkspaceId());

  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    listWorkspaces()
      .then((list) => {
        setItems(list);
        // Se não há gestão ativa salva, usa a padrão (ou a primeira)
        if (!getActiveWorkspaceId() && list.length) {
          const def = list.find((w) => w.isDefault) ?? list[0];
          setActiveWorkspaceId(def.companyId);
          setActiveId(def.companyId);
        }
      })
      .catch(() => {});
  }, []);

  const active = items.find((w) => w.companyId === activeId) ?? items.find((w) => w.isDefault) ?? items[0];

  const switchTo = (companyId: string) => {
    setOpen(false);
    if (companyId === activeId) return;
    setActiveWorkspaceId(companyId);
    // Recarrega para refazer todas as consultas de finanças com a nova gestão
    window.location.reload();
  };

  const handleCreate = async () => {
    if (!name.trim()) return;
    try {
      setCreating(true);
      const ws = await createWorkspace(name.trim());
      setActiveWorkspaceId(ws.companyId);
      window.location.reload();
    } catch (err: any) {
      openSnackbar({
        open: true,
        message: err?.response?.data?.message || 'Erro ao criar gestão',
        variant: 'alert',
        alert: { color: 'error' }
      } as any);
      setCreating(false);
    }
  };

  if (!items.length) return null;

  return (
    <Box sx={{ flexShrink: 0 }}>
      <Button
        ref={anchorRef}
        onClick={() => setOpen((o) => !o)}
        color="secondary"
        variant="outlined"
        size="small"
        startIcon={<TeamOutlined />}
        endIcon={<DownOutlined style={{ fontSize: 10 }} />}
        sx={{ textTransform: 'none', maxWidth: 220, color: 'text.primary', borderColor: 'divider' }}
      >
        <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
          {active?.name ?? 'Gestão'}
        </Typography>
      </Button>

      <Menu
        anchorEl={anchorRef.current}
        open={open}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { minWidth: 240, mt: 0.5 } } }}
      >
        <Typography variant="caption" sx={{ px: 2, py: 0.5, color: 'text.secondary', display: 'block' }}>
          Suas gestões
        </Typography>
        {items.map((w) => (
          <MenuItem key={w.companyId} selected={w.companyId === active?.companyId} onClick={() => switchTo(w.companyId)}>
            <ListItemIcon sx={{ minWidth: 28 }}>
              {w.companyId === active?.companyId ? <CheckOutlined style={{ fontSize: 14 }} /> : null}
            </ListItemIcon>
            <Typography variant="body2" noWrap>
              {w.name}
              {w.isOwner ? '' : ' (compartilhada)'}
            </Typography>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          onClick={() => {
            setOpen(false);
            setName('');
            setDialogOpen(true);
          }}
        >
          <ListItemIcon sx={{ minWidth: 28 }}>
            <PlusOutlined style={{ fontSize: 14 }} />
          </ListItemIcon>
          <Typography variant="body2">Nova gestão</Typography>
        </MenuItem>
      </Menu>

      <Dialog open={dialogOpen} onClose={() => !creating && setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Nova gestão</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Nome da gestão"
            placeholder="Ex.: Família, Pessoal, Empresa…"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={creating} color="secondary">
            Cancelar
          </Button>
          <Button onClick={handleCreate} variant="contained" disabled={creating || !name.trim()}>
            {creating ? <CircularProgress size={18} /> : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
